import { useEffect, useState } from 'react';
import { InfoCard, Link, Progress, Table, TableColumn, WarningPanel } from '@backstage/core-components';
import { githubAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';

// Mismo criterio que DORA: el CD de un msa corre por tag; v* = prod, bv* = qa, el resto (av*) = dev.
const CD_WORKFLOW = 'github-action-cd.yml';
const envFromTag = (tag: string) => {
  if (tag.startsWith('v')) return 'prod';
  return tag.startsWith('bv') ? 'qa' : 'dev';
};

type Run = {
  id: number;
  head_branch: string | null;
  conclusion: string | null;
  status: string;
  run_started_at: string;
  html_url: string;
  event: string;
  triggering_actor?: { login: string };
};

type Row = { id: number; date: string; env: string; tag: string; who: string; result: string; url: string };

const RESULT: Record<string, string> = {
  success: 'Desplegado',
  failure: 'Falló',
  cancelled: 'Cancelado',
  action_required: 'Esperando aprobación',
};

const columns: TableColumn<Row>[] = [
  { title: 'Fecha', field: 'date' },
  { title: 'Ambiente', field: 'env' },
  { title: 'Versión', field: 'tag', render: row => <Link to={row.url}>{row.tag}</Link> },
  { title: 'Quién', field: 'who' },
  { title: 'Resultado', field: 'result' },
];

/** Últimos despliegues del servicio: corridas del CD Workflow con su tag, quién lo lanzó y cómo terminó. */
export const DeploymentHistoryCard = () => {
  const { entity } = useEntity();
  const githubAuth = useApi(githubAuthApiRef);
  const slug = entity.metadata.annotations?.['github.com/project-slug'];
  const [rows, setRows] = useState<Row[]>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!slug) return;
    (async () => {
      const token = await githubAuth.getAccessToken(['repo']);
      const res = await fetch(`https://api.github.com/repos/${slug}/actions/workflows/${CD_WORKFLOW}/runs?per_page=30`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/vnd.github+json' },
      });
      if (res.status === 404) {
        setRows([]); // el repo no tiene CD Workflow
        return;
      }
      if (!res.ok) throw new Error(`GitHub respondió ${res.status}`);
      const body = (await res.json()) as { workflow_runs: Run[] };
      // ponytail: el resultado es el de la corrida completa, no el del job sync_tag (DORA sí lo distingue).
      setRows(
        body.workflow_runs.map(run => {
          const tag = run.head_branch ?? '';
          return {
            id: run.id,
            date: new Date(run.run_started_at).toLocaleString('es-EC'),
            env: run.event === 'push' ? envFromTag(tag) : `manual (${tag})`,
            tag,
            who: run.triggering_actor?.login?.replace(/_deuna$/, '') ?? '-',
            result: run.status !== 'completed' ? 'En curso' : RESULT[run.conclusion ?? ''] ?? run.conclusion ?? '-',
            url: run.html_url,
          };
        }),
      );
    })().catch(e => setError(String(e?.message ?? e)));
  }, [slug, githubAuth]);

  if (!slug) return null;
  if (error) return <WarningPanel title="No se pudo leer el historial de despliegues" message={error} />;
  if (!rows) return <InfoCard title="Historial de despliegues"><Progress /></InfoCard>;
  return (
    <Table<Row>
      title="Historial de despliegues"
      subtitle={`Últimas ${rows.length} corridas de ${CD_WORKFLOW}`}
      options={{ paging: true, pageSize: 10, search: true, padding: 'dense' }}
      columns={columns}
      data={rows}
      emptyContent={<div style={{ padding: 16 }}>Sin corridas de {CD_WORKFLOW} en este repo.</div>}
    />
  );
};
